import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import capitalize from 'lodash-es/capitalize';
import {BehaviorSubject, combineLatest, Observable, Subject, Subscription, of} from 'rxjs';
import {catchError, filter, map, switchMap, tap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {AccountType} from '../../../model';
import {cache, MIN_START_DATE} from '../../../utils';
import {AnalyticsService} from '../../shared/analytics.service';
import {HttpError} from '../../shared/api.service';
import {AppStateService} from '../../shared/app-state.service';
import {AuthService} from '../../shared/auth.service';
import {FirstNamePipe} from '../../shared/first-name.pipe';
import {NameFormatPipe} from '../../shared/name-format.pipe';
import {NavigationService} from '../../shared/navigation.service';
import {ClientApiService} from '../client-api.service';
import {getActivityType, getPositionStatus, PositionStatus} from '../position-search.pipe';
import {TermsModalComponent} from '../terms-modal/terms-modal.component';
import {TrackerActivityPipe} from '../tracker-activity.pipe';

@Component({
  selector: 'home-page',
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.scss']
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomePageComponent implements OnInit, OnDestroy {

  @ViewChild('termsModal') termsModal: TermsModalComponent;

  salaryGuide = environment.flags.salaryGuide;
  aquentsBookPromotion = environment.flags.aquentsBookPromotion;
  personaPromotion = environment.flags.personaPromotion;

  user$: Observable<User>;
  brand$ = this.AppState.brand$;
  locale$ = this.AppState.locale$;
  isAustralia$ = this.AppState.isAustralia$;

  timecardsReload$ = new Subject<void>();
  timecardsLoading$ = new BehaviorSubject(true);
  timecards$: Observable<Timecard[]>;

  reloadPositions$ = new Subject<void>();
  positionsLoading$ = new BehaviorSubject(true);
  positions$: Observable<Order[]>;
  agentFirst$: Observable<string>;

  poolsLoading$ = new BehaviorSubject(true);
  pools$: Observable<Order[]>;

  PositionStatus = PositionStatus;
  minStartDate = MIN_START_DATE;
  maxEndDate = new Date().setHours(0, 0, 0, 0);

  timecardsOnly$ = this.Auth.user$.pipe(map(user => user && user.accountTypeId === AccountType.TimecardOnly));
  masquerading$ = this.Auth.masquerading$;

  capitalize = capitalize;

  private positionsSub: Subscription;
  private timecardsSub: Subscription;

  constructor(private Auth: AuthService,
              private Api: ClientApiService,
              private Navigation: NavigationService,
              private AppState: AppStateService,
              private ActivityPipe: TrackerActivityPipe,
              private Analytics: AnalyticsService) {
  }

  ngOnInit() {
    this.user$ = this.Auth.user$.pipe(filter(v => !!v));

    this.positions$ = this.reloadPositions$
      .pipe(
        tap(() => this.positionsLoading$.next(true)),
        switchMap(() => this.Api.getPositions()),
        tap(() => this.positionsLoading$.next(false)),
        catchError((err: HttpError) => {
          console.error('Failed to load positions.', err);
          this.Navigation.handleError(err);
          return of(null);
        }),
        cache()
      );

    // Trigger initial HTTP request
    this.positionsSub = this.positions$.subscribe();
    this.reloadPositions$.next();

    this.pools$ = this.Api.getPools()
      .pipe(
        map(v => v.orders),
        tap(() => this.poolsLoading$.next(false)),
        catchError((err: HttpError) => {
          console.error('Failed to load pools.', err);
          this.Navigation.handleError(err);
          return of(null);
        }),
        cache()
      );

    this.timecards$ = this.timecardsReload$
      .pipe(
        tap(() => this.timecardsLoading$.next(true)),
        switchMap(() => this.Api.getUnsignedTimecards()),
        tap(() => this.timecardsLoading$.next(false)),
        catchError((err: HttpError) => {
          console.error('Failed to load timecards.', err);
          this.Navigation.handleError(err);
          return of(null);
        }),
        cache()
      );

    // Trigger initial HTTP request
    this.timecardsSub = this.timecards$.subscribe();
    this.timecardsReload$.next();

    this.agentFirst$ = combineLatest(this.user$, this.AppState.locale$, (user, locale) => {
      const agent = user.staff && user.staff.find(v => v.agent);
      if (agent) return FirstNamePipe.apply(NameFormatPipe.apply(agent, locale), locale);
    });
    this.Auth.user$.subscribe(user => {
      if (!user.tutorialSeen) {
        this.Api.markNewContactAsRegistered(user.personId).subscribe();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.positionsSub) this.positionsSub.unsubscribe();
    if (this.timecardsSub) this.timecardsSub.unsubscribe();
  }

  getPositionLink(order: Order) {
    return getPositionStatus(order) === PositionStatus.Open ? 'candidates' : 'details';
  }

  getPositionStatus(order: Order) {
    return getPositionStatus(order);
  }

  getActivityType(status: PositionStatus, order: Order): string {
    return getActivityType(status, order, this.ActivityPipe.transform(order.latestTrackerActivity));
  }

  showTermsModal(event: Event, timecard: Timecard) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.termsModal.show([timecard.timecardId]);
  }

  signTimecardComplete() {
    this.timecardsReload$.next();
  }

  qccSuccess() {
    this.reloadPositions$.next();
    this.Analytics.trackEvent('Home page: Review submitted');
  }

  onAssignmentUpdated() {
    this.reloadPositions$.next();
    this.Analytics.trackEvent('Home page: Assignment updated');
  }

  createLeads() {
    this.Api.createEloquaLead().subscribe();
  }

  isNotOrderRequest(order: Order) {
    return !order.isRequest;
  }
}
